/**
 * pages/hazard/Triage.js — АЮУЛЫН МЭДЭЭЛЛИЙГ АНГИЛАХ (эзэн, хугацаа өгөх).
 *
 * Зам: /hazard/triage
 *
 * ⚠ ЯАГААД ЭНЭ ХУУДАС ХЭРЭГТЭЙ БОЛОВ (2026-08-13):
 *   Ирсэн мэдээллийн ~94% нь «Шинэ» дээр хуримтлагдсан байв. Шалтгааныг
 *   зан үйл дутсанд буруутгаж байсан ч кодыг шалгахад ӨӨР зүйл илэрсэн:
 *
 *     `assignHazard()` API клиент болон backend endpoint БАЙГАА мөртлөө
 *     түүнийг дуудаж байгаа ДЭЛГЭЦ ОГТ БАЙГААГҮЙ. `/hazard/:id` дээр
 *     зөвхөн «Шийдвэрлэх» ба «Татгалзах» товч бий.
 *
 *   Өөрөөр хэлбэл моделд `NEW → ASSIGNED → COMPLETED` урсгал байсан ч
 *   UI-аас гарах цорын ганц зам нь `NEW → COMPLETED` байсан тул бүх зүйл
 *   «Шинэ» дээр үлддэг байв. Долоо хоног тутмын 30 минутын зан үйл
 *   эхлүүлэхийн тулд ЭНЭ ДЭЛГЭЦ ХЭРЭГТЭЙ.
 *
 * ЭЗЭМШИГЧ: ХАБ хэлтсийн мэргэжилтэн хуваарилна. Цех нь хуваарилагдсаны
 * дараа гүйцэтгэж, буцаад хяналт хийнэ.
 *
 * ХЭМЖҮҮР: «Хүлээж буй %» — 94%-аас 40%-аас доош. Энэ тоо харагдахгүй
 * бол ажил гурав дахь долоо хоногтоо унтарна.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Input,
  Modal,
  Progress,
  Row,
  Segmented,
  Select,
  Spin,
  Tag,
  Tooltip,
  message,
} from "antd";
import dayjs from "dayjs";

import {
  getHazards,
  assignHazard,
  assignHazardUnit,
  getCategories,
  getTriageSummary,
} from "src/api/hazard";
import { xabInspectionApi } from "src/api/xabApi";
import PageHelp from "src/components/help/PageHelp";
import DepartmentTseh from "src/components/departmentTseh";
import DepartmentNegj from "src/components/departmentNegj";
import { useUserContext } from "src/contexts/userContext";
import { resolveViolationMode } from "src/components/inspection/stageAccess";
import { phoneAiApi } from "src/api/phoneAiApi";

/* Ажилтны цээж зураг — ТН-ЭЭР нэрлэгдсэн MinIO хувилбар.
   ⚠ ХСЕ БААЗАД БАЙХГҮЙ: HR-ийн 63 талбар, ажилтны хүснэгтүүдэд
     зургийн багана алга — тийм учраас баазаас хайхад олдоогүй.
     Эх сурвалж нь `WorkerLookupSection.js`, 71300-ийн бүртгэл,
     ослын маягттай ИЖИЛ.
   ⚠ Зураггүй ажилтан бий тул `onError`-оор ЧИМЭЭГҮЙ нуудаг —
     эвдэрсэн зургийн дүрс гарвал карт эмх замбараагүй харагдана. */
const EMP_PHOTO = "https://minio-action.erdenetmc.mn/emp/";

const BRAND = "#B5251D";

/** Ноцтой байдлаас арилгах хугацаа санал болгоно (backend-тэй ижил дүрэм). */
const SUGGEST_DAYS = { 1: 7, 2: 3, 3: 1, 4: 1, 5: 1 };

/**
 * Хуваарилсан ажил ЯМАР ХЭЛБЭРЭЭР гүйцэтгэгдэх вэ.
 *
 * ⚠ Энэ сонголт нь ажлыг ТУХАЙН БҮРТГЭЛИЙН ХУУДСАНД «надад ирсэн ажил»
 *   болгож харуулна. Сонгохгүй бол хариуцагч зөвхөн «Миний даалгавар»
 *   дээрээс харна — наряд/ӨЭА үүсгэх алхам холбогдохгүй.
 */
const WORK_TYPES = [
  {
    value: "OEA",
    label: "ӨЭА наряд",
    hint: "Өндөр эрсдэлт ажил — /highrisk/register дээр наряд нээнэ",
    color: "#B5251D",
  },
  {
    value: "NARYAD",
    label: "Ажлын даалгавар",
    hint: "Ердийн ажлын даалгавар — /naryad/register дээр ажил үүсгэнэ",
    color: "#0F6E56",
  },
];

const WORK_LABEL = Object.fromEntries(
  WORK_TYPES.map((w) => [w.value, w.label]),
);
const WORK_COLOR = Object.fromEntries(
  WORK_TYPES.map((w) => [w.value, w.color]),
);

const SEVERITY = {
  1: { name: "Бага", color: "#16A34A" },
  2: { name: "Дунд", color: "#CA8A04" },
  3: { name: "Өндөр", color: "#EA580C" },
  // ⚠ ХУУЧИН 4/5 — сонголтод ГАРАХГҮЙ, зөвхөн баазад хоцорсон мөрийг
  //   «Өндөр» гэж зөв харуулахын тулд үлдээв (ХООСОН харагдах ёсгүй).
  4: { name: "Өндөр", color: "#DC3545" },
  5: { name: "Өндөр", color: "#DC3545" },
};

function Kpi({ label, value, color, hint }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E6E9EF",
        borderRadius: 10,
        padding: "10px 12px",
        height: "100%",
      }}
    >
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#1F2430" }}>
        {label}
      </div>
      {hint ? (
        <div style={{ fontSize: 10.5, color: "#9AA3B0", marginTop: 2 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/** Хэдэн хоног хүлээсэн — хуучрахын хэрээр улаан болно. */
function WaitedFor({ date }) {
  if (!date) return null;
  const days = dayjs().diff(dayjs(date), "day");
  const color = days >= 30 ? "#DC2626" : days >= 14 ? "#EA580C" : "#8A94A6";
  return (
    <Tooltip title={`Мэдээлэгдсэн: ${dayjs(date).format("YYYY-MM-DD HH:mm")}`}>
      <span style={{ fontSize: 11.5, fontWeight: 700, color }}>
        {days} хоног хүлээсэн
      </span>
    </Tooltip>
  );
}

export default function HazardTriage() {
  const navigate = useNavigate();
  const { user, isAdmin: isAdminFn } = useUserContext();

  // ⚠ ХАБ хэлтсийн мэргэжилтэн (4 дүгээр шат) ба админ л хуваарилна.
  //   Backend мөн шалгана — энд зөвхөн дэмий товч гаргахгүйн тулд.
  const mode = resolveViolationMode(user, !!(isAdminFn && isAdminFn()));
  const canAssign = mode === "hab_dept_violation" || mode === "all";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState([]);

  // ── ЖАГСААЛТЫН ШҮҮЛТ ────────────────────────────────────────────────
  // ⚠ Цех/хэсэг нь СЕРВЕР талд шүүгдэнэ. Клиент дээр шүүвэл 100 мөрийн
  //   хязгаараас цааш байгаа мөрүүд огт ирэхгүй тул «энэ цехэд юу ч алга»
  //   гэсэн ХУДАЛ дүр зураг гарна.
  const [fTseh, setFTseh] = useState(null);
  const [fTsehParent, setFTsehParent] = useState(null);
  const [fNegj, setFNegj] = useState(null);
  const [fSeverity, setFSeverity] = useState(null);
  const [fSearch, setFSearch] = useState("");
  /* ── ХУРЛЫН ШҮҮЛТҮҮД (2026-10-28, хэрэглэгчийн хүсэлт) ────────
     «Ирсэн мэдээллийг хугацаа заагаад татаж харж болдог болох.
      Жишээ нь хурлыг 7 хоногоор явуулдаг.»
     ⚠ БҮГД СЕРВЕР талд шүүгдэнэ. Клиент дээр шүүвэл `per_page=100`
       хязгаараас болж шүүлтээс ГАДНА үлдсэн мөрүүд хэзээ ч
       татагдахгүй — хурал дээр тоо дутуу гарна. */
  const [fRange, setFRange] = useState(null);
  const [fCat, setFCat] = useState(null);
  /* ⚠ ХАРИУЦСАН нэгж — `fTseh` (МЭДЭЭЛЭГЧИЙН нэгж)-ЭЭС ТУСДАА.
     Хоёр өөр тэнхлэг: хэн мэдээлсэн ба хэн хариуцаж байгаа. */
  const [fRespBn, setFRespBn] = useState(null);
  const [cats, setCats] = useState([]);
  /* Хуваарилах цонхны ХЭЛБЭР: нэгжид эсвэл хүнд */
  const [assignMode, setAssignMode] = useState("person");
  const [uTseh, setUTseh] = useState(null);
  const [uTsehParent, setUTsehParent] = useState(null);
  const [uNegj, setUNegj] = useState(null);
  /* ⚠ ЦЕХ ба ХЭСГИЙН нэрийг ТУСДАА (2026-11-05). Өмнө нь нэг
     `uName` байсан тул хэсгийг цэвэрлэхэд хэсгийн нэр УГЖИЖ үлдэж,
     цех рүү буцахгүй байв. */
  const [uTsehName, setUTsehName] = useState(null);
  const [uNegjName, setUNegjName] = useState(null);
  /* Серверт явах нэр — хэсэг сонгосон бол ТҮҮНИЙ нэр, эс бөгөөс цехийн. */
  const uName = uNegjName || uTsehName;
  /* ── ХАРИУЦАХ НЭГЖИЙН AI САНАЛ (2026-11-01) ───────────────────────
     Хуучин 71300-ийн 200 бүртгэл дээр хэмжсэн: 1-р санал 78.5%, 3
     саналын аль нэг 96% хүний шийдвэртэй таарсан. АВТОМАТААР
     ХУВААРИЛАХГҮЙ — товчоор сонгоод ХАБЭА өөрөө батална. */
  const [aiSug, setAiSug] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiErr, setAiErr] = useState(null);

  // Хуваарилах цонх — нэг мөр эсвэл бөөнөөр
  const [assignOf, setAssignOf] = useState(null);   // мөр | "bulk"
  const [tn, setTn] = useState(null);
  const [due, setDue] = useState(null);
  const [note, setNote] = useState("");
  const [workType, setWorkType] = useState(null);
  const [busy, setBusy] = useState(false);

  // ⚠ ХОЁР ХАРАГДАЦ: «Хүлээгдэж буй» (эзэнгүй) ба «Хуваарилсан» (эзэнтэй).
  //   Хоёрдугаарыг харуулахгүй бол хуваарилагч ажлаа ӨГӨӨД мартана —
  //   биелэлт хянагдахгүй, дараа жил дахин ижил аюул гарна.
  const [tab, setTab] = useState("waiting");

  // Ажилтны хайлт — ЦЕХ → ХЭСЭГ → ажилтан гэсэн дараалалтай.
  // ⚠ Өмнө нь зөвхөн нэрээр хайдаг байсан тул хуваарилагч ажилтны нэрийг
  //   МЭДЭЖ байх шаардлагатай байв. Бодит ажилд «энэ бол 25-р цехийн
  //   асуудал, тэндээс хэн нэгэн» гэж эхэлдэг тул нэгжээс нь эхлүүлэв.
  const [workers, setWorkers] = useState([]);
  const [wLoading, setWLoading] = useState(false);
  const [tseh, setTseh] = useState(null);
  const [tsehParent, setTsehParent] = useState(null);
  const [negj, setNegj] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      // ⚠ Хамгийн ХУУЧНААР нь эхэлнэ. Шинээр ирсэнийг эхэнд тавибал
      //   хамгийн удаан хүлээсэн мэдээлэл мөнхөд доор үлдэнэ.
      // ⚠ `per_page` нь сервер талд 100-аар ХЯЗГААРЛАГДСАН. Түүнээс их
      //   утга дамжуулбал чимээгүй тайрагдана — тиймээс 100 гэж ИЛ бичив
      //   бөгөөс нийт тоог доор харуулж, шүүлтээр нарийсгахыг санана.
      getHazards({
        /* ── ГУРВАН ТАБ = ХОЁР ШАТЛАЛТ УРСГАЛ (2026-10-28) ─────
           waiting — эзэнгүй, НЭГЖ ч тодорхойгүй → ХАБ хэлтсийн ажил
           unit    — эзэнгүй, НЭГЖ тодорхой    → нэгжийн ХАБЭА-гийн
           assigned— эзэнтэй                    → мөшгих
           ⚠ `waiting` дээр `unit_unassigned` ЗААВАЛ: үүнгүйгээр
             нэгжид хуваарилагдсан мөр ХОЁР табад зэрэг гарч, ХАБ
             хэлтэс түүнийг дахин хуваарилах гээд байна — яг тэр
             «олон хуваарилах үйлдэл давтагдах» гомдол. */
        ...(tab === "assigned"
          ? { assigned: 1 }
          : tab === "unit"
          ? { unassigned: 1, unit_assigned: 1 }
          : { unassigned: 1, unit_unassigned: 1 }),
        hazards_only: 1,
        per_page: 100,
        dept_code: fNegj || fTseh || undefined,
        responsible_bn_code: fRespBn || undefined,
        hazard_category_id: fCat || undefined,
        severity: fSeverity || undefined,
        search: fSearch.trim() || undefined,
        /* Огнооны муж — СЕРВЕР талд (`report_date`). Сервер нь
           дуусах өдрийг БҮТНЭЭР хамруулдаг. */
        date_from: fRange?.[0]
          ? fRange[0].format("YYYY-MM-DD")
          : undefined,
        date_to: fRange?.[1] ? fRange[1].format("YYYY-MM-DD") : undefined,
      }),
      getTriageSummary(90).catch(() => null),
    ])
      .then(([list, sum]) => {
        const rows = list?.items || list?.data || [];
        // Хүлээгдэж буй — хамгийн ХУУЧНААР. Хуваарилсан — хугацаа нь
        // хамгийн ойрхноор (яаралтай нь дээр).
        if (tab === "waiting") {
          rows.sort((a, b) =>
            String(a.report_date || "").localeCompare(
              String(b.report_date || ""),
            ),
          );
        } else {
          rows.sort((a, b) =>
            String(a.sla_deadline || "9999").localeCompare(
              String(b.sla_deadline || "9999"),
            ),
          );
        }
        setItems(rows);
        setTotal(list?.total ?? rows.length);
        setSummary(sum);
      })
      .catch((e) =>
        message.error(
          e?.response?.data?.message || "Жагсаалт татаж чадсангүй",
        ),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fTseh, fNegj, fSeverity, fRespBn, fCat, fRange, tab]);

  useEffect(() => {
    load();
  }, [load]);

  /* Аюулын ангиллын лавлах — шүүлтийн сонголтод.
     ⚠ ЛАВЛАХААС, ачаалсан мөрүүдээс БИШ: шүүлт нь сервер талд тул
       мөрүүдээс гаргавал шүүлт тавьсны дараа сонголт хумигдана. */
  useEffect(() => {
    getCategories()
      .then((d) => setCats(d?.items || d?.categories || d || []))
      .catch(() => setCats([]));
  }, []);

  /** Шүүлт солигдоход сонголтыг ЦЭВЭРЛЭНЭ — харагдахгүй мөр сонгогдсон
   *  хэвээр үлдвэл «бөөнөөр хуваарилах» нь харагдахгүй мөрийг оруулна. */
  useEffect(() => {
    setPicked([]);
  }, [fTseh, fNegj, fSeverity, fSearch, tab]);

  /**
   * Ажилтны жагсаалтыг татна.
   *
   * ⚠ ХЭСЭГ сонгогдсон бол ТҮҮГЭЭР, эс бөгөөс ЦЕХЭЭР шүүнэ. Аль нь ч
   *   сонгогдоогүй үед хоосон жагсаалт харуулна — бүх үйлдвэрийн 22 000
   *   ажилтныг татаж, дунд нь хайлгах нь ашиггүй.
   */
  const searchWorkers = (q, opts = {}) => {
    const bn = opts.tseh !== undefined ? opts.tseh : tseh;
    const sec = opts.negj !== undefined ? opts.negj : negj;
    if (!bn && !sec && !q) {
      setWorkers([]);
      return;
    }
    setWLoading(true);
    xabInspectionApi
      .searchWorkers({
        q: q || undefined,
        bn_code: bn || undefined,
        tseh_code: sec || undefined,
        limit: 100,
      })
      .then((d) => setWorkers(d?.workers || []))
      .catch(() => setWorkers([]))
      .finally(() => setWLoading(false));
  };

  const openAssign = (row) => {
    setAssignOf(row);
    setTn(null);
    setNote("");
    setWorkType(null);
    // ⚠ Хугацааг САРААР авна. Аюул арилгах ажил нь ихэвчлэн засварын
    //   хуваарь, сэлбэгийн нийлүүлэлттэй уягддаг тул «яг тэр өдөр» гэж
    //   тохирох нь бодит бус. Сар сонгоход тэр САРЫН СҮҮЛИЙН ӨДӨР
    //   хугацаа болно.
    setAssignMode("person");
    const days = row === "bulk" ? 3 : SUGGEST_DAYS[row?.severity] ?? 3;
    setDue(dayjs().add(days, "day").endOf("month"));
    setWorkers([]);
    // Мэдээлэл ирсэн цехийг анхдагчаар — ихэнх тохиолдолд тэр л зөв
    const dept = row === "bulk" ? null : row?.insert_dept_code || null;
    setTseh(dept);
    setTsehParent(null);
    setNegj(null);
    if (dept) searchWorkers("", { tseh: dept, negj: null });
  };

  /* ── НЭГЖИД ХУВААРИЛАХ цонхыг нээнэ (ХАБ хэлтэс) ─────────────
     ⚠ Анхдагчаар МЭДЭЭЛЭГЧИЙН нэгжийг тавина — ихэнх тохиолдолд
       аюул нь мэдээлсэн нэгжид байдаг. Гэхдээ ЗАСАЖ болно: БҮ-ийн
       аюулыг Цахилгаан цех арилгах тохиолдол бий. */
  const openUnitAssign = (row) => {
    setAssignMode("unit");
    setAssignOf(row);
    setNote("");
    const dept = row === "bulk" ? null : row?.insert_dept_code || null;
    setUTseh(dept);
    setUTsehParent(null);
    setUNegj(null);
    setUNegjName(null);
    setUTsehName(row === "bulk" ? null : row?.insert_dept_name || null);
    setAiSug(null);
    setAiErr(null);
    if (row && row !== "bulk" && row.id) {
      setAiLoading(true);
      phoneAiApi
        .routeSuggest(row.id)
        .then(setAiSug)
        .catch((e) => setAiErr(e?.response?.data?.message || "AI санал гарсангүй"))
        .finally(() => setAiLoading(false));
    }
  };

  /** AI-ийн саналыг сонгогчид тавина (ХАБЭА «Нэгжид хуваарилах»-аар батална). */
  const pickAiUnit = (code, name) => {
    setUTseh(code);
    setUTsehParent(null);
    setUNegj(null);
    setUNegjName(null);
    setUTsehName(name || null);
  };

  /* Нэгжид хуваарилах — ХҮН рүү хуваарилахаас ТУСДАА дуудлага.
     ⚠ Нэг мөр унасан нь бусдыг зогсоохгүй (бөөн хуваарилалт). */
  const doAssignUnit = async () => {
    const code = uNegj || uTseh;
    if (!code) return message.warning("Нэгжээ сонгоно уу");
    const targets = assignOf === "bulk" ? picked : [assignOf.id];
    if (!targets.length) return message.warning("Мөр сонгогдоогүй байна");
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (const id of targets) {
      try {
        await assignHazardUnit(
          id,
          code,
          uName || undefined,
          note.trim() || undefined,
        );
        ok += 1;
      } catch (e) {
        failed.push(id);
      }
    }
    setBusy(false);
    if (ok) message.success(`${ok} мэдээлэл нэгжид хуваарилагдлаа`);
    if (failed.length)
      message.error(`${failed.length} мөр хуваарилагдсангүй`);
    setAssignOf(null);
    setPicked([]);
    load();
  };

  const doAssign = async () => {
    if (!tn) return message.warning("Хариуцагчаа сонгоно уу");
    const targets = assignOf === "bulk" ? picked : [assignOf.id];
    if (!targets.length) return message.warning("Мөр сонгогдоогүй байна");
    setBusy(true);
    // Сарын сүүлийн өдөр — «тэр сард багтаана» гэсэн утга
    const dueTxt = due ? due.endOf("month").format("YYYY-MM-DD") : undefined;
    let ok = 0;
    const failed = [];
    for (const id of targets) {
      try {
        await assignHazard(id, tn, note.trim() || undefined, dueTxt, workType);
        ok += 1;
      } catch (e) {
        // ⚠ Нэг мөр унасан нь бусдыг зогсоох ёсгүй — 30 мөр хуваарилж
        //   байгаад 5 дахь дээр бүхэлдээ таслах нь ажлыг үрэгдүүлнэ.
        failed.push(id);
      }
    }
    setBusy(false);
    if (ok) message.success(`${ok} мэдээлэл хуваарилагдлаа`);
    if (failed.length) message.error(`${failed.length} мөр хуваарилагдсангүй`);
    setAssignOf(null);
    setPicked([]);
    load();
  };

  const workerOptions = useMemo(
    () =>
      workers.map((w) => ({
        value: w.tn,
        label: `${w.name || "?"} · ${w.tn}${
          w.position ? " · " + w.position : ""
        }`,
      })),
    [workers],
  );

  /* ── МӨР ТУС БҮРД ҮЙЛДЭЛ ХИЙЖ БОЛОХ ЭСЭХ ───────────────────────
     Таб бүрд ӨӨР эрх хэрэгтэй:
       «Нэгж тодорхойлох»    → `can_assign_unit` (ХАБ хэлтэс)
       «Нэгжид хуваарилсан»  → `can_assign` (нэгжийн ХАБЭА, 179)
     ⚠ Серверээс ирсэн тугт НАЙДНА, клиент дээр дүрмийг ДАВХАР
       бичихгүй — хоёр тал зөрвөл товч нь урхи болно
       ([[feedback_per_row_permission_flag]]). */
  /* ── ЭРХИЙГ СЕРВЕРЭЭС (2026-10-28) ──────────────────────────────
     ⚠ Клиент талын `canAssign` нь `resolveViolationMode`-оос гардаг
       бөгөөд НЭГЖИЙН ХАБЭА (бүлэг 179)-г эрхгүй гэж үздэг. Тэр нь
       одоо БУРУУ: 179 нь нэгж дотроо хуваарилна. Серверийн туг л
       эрхийн ҮНЭН эх сурвалж.
     ⚠ `summary` ачаалагдах хүртэл клиентийн утгыг нөөц болгоно — эс
       бөгөөс хуудас нээгдэх үед товчнууд хэсэг хугацаанд алга
       болно. */
  const canUnitAssign = summary?.can_assign_unit ?? canAssign;
  const isUnitHse = !!summary?.is_unit_hse;

  /* ⚠ Таб солиход сонголтыг ХҮЧИНГҮЙ болгоно: «Нэгж тодорхойлох»
     табад сонгосон мөрүүд «Нэгжид хуваарилсан» табад ӨӨР үйлдэлд
     (хүнд хуваарилах) өртөх байсан. */
  useEffect(() => {
    setPicked([]);
  }, [tab]);

  const rowCanAct = (it) => {
    if (!it) return false;
    if (tab === "waiting") return !!it.can_assign_unit;
    if (tab === "unit") return !!it.can_assign;
    return false;
  };

  const pct = summary?.new_pct;
  // Зорилт: 40%-аас доош. Өнгө нь ахицыг НЭГ ХАРЦААР хэлнэ.
  const pctColor =
    pct == null ? "#8A94A6" : pct < 40 ? "#16A34A" : pct < 70 ? "#EA580C" : "#DC2626";

  return (
    <div style={{ padding: 16, background: "#F1F3F6", minHeight: "100%" }}>
      {/* Толгой */}
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND} 0%, #7A1A14 100%)`,
          borderRadius: 14,
          padding: "16px 18px",
          marginBottom: 14,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <i className="fas fa-inbox" style={{ fontSize: 24 }} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 500,
              color: "rgba(255,255,255,0.9)",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            Ангилах дараалал
            <PageHelp topic="hazard/triage" light />
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.85 }}>
            Эзэн, хугацаа хүлээгээгүй аюулын мэдээлэл · хамгийн хуучнаар нь
          </p>
        </div>
        <Button
          onClick={load}
          style={{
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.55)",
          }}
        >
          <i className="fas fa-sync-alt" style={{ marginRight: 6 }} />
          Шинэчлэх
        </Button>
      </div>

      {/* ⚠ ХОЁР ШАТЛАЛТ урсгалд «эрх алга» гэдэг нь нэгжийн ХАБЭА-д
          БУРУУ мессеж байсан — тэр нэгж дотроо хуваарилдаг. */}
      {!canUnitAssign && !isUnitHse ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Танд хуваарилах эрх алга"
          description={
            "ХАБ хэлтэс мэдээллийг хариуцах НЭГЖИЙГ тодорхойлно, тэр " +
            "нэгжийн ХАБЭА инженер дотроо гүйцэтгэгчээ оноодог. Та " +
            "жагсаалтыг харах боломжтой."
          }
        />
      ) : null}
      {!canUnitAssign && isUnitHse ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Та нэгж дотроо хуваарилна"
          description={
            "«Нэгжид хуваарилсан» табаас өөрийн нэгжид оногдсон " +
            "мэдээллийг харж, гүйцэтгэгчээ оноорой. Хариуцах нэгжийг " +
            "ХАБ хэлтэс тодорхойлно."
          }
        />
      ) : null}

      {/* ── ХЭМЖҮҮР ────────────────────────────────────────────────────
          ⚠ Энэ мөр нь чимэглэл БИШ. «Хүлээж буй %» буурч байгааг харахгүй
          бол долоо хоног тутмын зан үйл 3 дахь долоо хоногтоо унтарна. */}
      <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Kpi
            label="Эзэн хүлээж буй"
            value={summary?.waiting ?? items.length}
            color="#DC2626"
            hint="энэ жагсаалт"
          />
        </Col>
        <Col xs={12} md={6}>
          <Kpi
            label="Эзэнтэй болсон"
            value={summary?.assigned ?? "—"}
            color="#16A34A"
          />
        </Col>
        <Col xs={12} md={6}>
          <Kpi
            label="Хугацаа хэтэрсэн"
            value={summary?.overdue ?? "—"}
            color="#7F1D1D"
          />
        </Col>
        <Col xs={12} md={6}>
          <div
            style={{
              background: "#fff",
              border: "1px solid #E6E9EF",
              borderRadius: 10,
              padding: "10px 12px",
              height: "100%",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: "#1F2430" }}>
              Хүлээж буй хувь
            </div>
            <Progress
              percent={pct ?? 0}
              strokeColor={pctColor}
              format={(v) => `${v}%`}
              size="small"
            />
            <div style={{ fontSize: 10.5, color: "#9AA3B0" }}>
              зорилт: 40%-аас доош
            </div>
          </div>
        </Col>
      </Row>

      <Segmented
        value={tab}
        onChange={setTab}
        style={{ marginBottom: 10 }}
        options={[
          { value: "waiting", label: "Нэгж тодорхойлох" },
          { value: "unit", label: "Нэгжид хуваарилсан" },
          { value: "assigned", label: "Хүнд хуваарилсан" },
        ]}
      />

      {/* ── ШҮҮЛТ ─────────────────────────────────────────────────── */}
      <Card
        size="small"
        style={{ marginBottom: 10, borderRadius: 10 }}
        bodyStyle={{ padding: "10px 12px" }}
      >
        <Row gutter={[8, 8]} align="bottom">
          <Col xs={24} md={6}>
            {/* ⚠ Энэ нь МЭДЭЭЛЭГЧИЙН нэгж (`dept_code` →
                `insert_dept_code`). Доорх «Хариуцсан нэгж»-ЭЭС ӨӨР
                тэнхлэг — хоёрыг хольвол хурал дээр буруу тоо
                гарна. */}
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 3 }}>
              Мэдээлэгчийн цех
            </div>
            <DepartmentTseh
              value={fTseh}
              noAutoSelect
              onChange={(code, parentId) => {
                setFTseh(code || null);
                setFTsehParent(parentId || null);
                setFNegj(null);   // цех солигдоход хэсэг хүчингүй
              }}
            />
          </Col>
          <Col xs={24} md={6}>
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 3 }}>
              Мэдээлэгчийн хэсэг
            </div>
            <DepartmentNegj
              value={fNegj}
              paranetid={fTsehParent}
              parentCode={fTseh}
              autoSelectOwn={false}
              onChange={(code) => setFNegj(code || null)}
            />
          </Col>
          <Col xs={12} md={4}>
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 3 }}>
              Ноцтой байдал
            </div>
            <Select
              allowClear
              style={{ width: "100%" }}
              value={fSeverity}
              onChange={(v) => setFSeverity(v ?? null)}
              placeholder="Бүгд"
              options={Object.keys(SEVERITY).map((k) => ({
                value: Number(k),
                label: SEVERITY[k].name,
              }))}
            />
          </Col>
          <Col xs={12} md={6}>
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 3 }}>
              Хайлт
            </div>
            <Input.Search
              allowClear
              value={fSearch}
              onChange={(e) => setFSearch(e.target.value)}
              onSearch={load}
              placeholder="Тайлбар, байршил, дугаар"
            />
          </Col>

          {/* ── ХУГАЦАА — хурал 7 хоногоор явдаг (2026-10-28) ────
              ⚠ СЕРВЕР талд шүүгдэнэ (`report_date`). Клиент дээр
                шүүвэл `per_page=100`-аас гадна үлдсэн мөр хэзээ ч
                татагдахгүй тул хурлын тоо дутуу гарна. */}
          <Col xs={24} md={7}>
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 3 }}>
              Хугацаа (мэдээлсэн)
            </div>
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              allowClear
              format="YYYY-MM-DD"
              placeholder={["Эхлэх", "Дуусах"]}
              value={fRange}
              onChange={(v) =>
                setFRange(v && v[0] && v[1] ? v : null)
              }
              ranges={{
                /* Хурлын анхдагч — сүүлийн 7 хоног */
                "7 хоног": [dayjs().add(-6, "day"), dayjs()],
                "30 хоног": [dayjs().add(-29, "day"), dayjs()],
                "Энэ сар": [
                  dayjs().startOf("month"),
                  dayjs().endOf("month"),
                ],
              }}
            />
          </Col>

          <Col xs={12} md={5}>
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 3 }}>
              Ангилал
            </div>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              value={fCat}
              onChange={(v) => setFCat(v ?? null)}
              placeholder="Бүгд"
              options={(cats || [])
                .filter((c) => c && (c.id ?? c.value))
                .map((c) => ({
                  value: c.id ?? c.value,
                  label: c.name ?? c.label,
                }))}
            />
          </Col>

          {/* ХАРИУЦСАН НЭГЖ — мэдээлэгчийн нэгжээс ТУСДАА тэнхлэг.
              «Мэдээллийг хариуцсан цех зэргийг тодорхой татаж
               хардаг болох» (хэрэглэгч, 2026-10-28). */}
          <Col xs={12} md={6}>
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 3 }}>
              Хариуцсан нэгж
            </div>
            <DepartmentTseh
              value={fRespBn}
              noAutoSelect
              onChange={(code) => setFRespBn(code || null)}
            />
          </Col>
          <Col xs={24} md={2}>
            {/* Шүүлт цэвэрлэх — ЗӨВХӨН icon. Текст нь шүүлтийн мөрөнд
                хэрэгтэй өргөнийг иддэг; үйлдэл нь ойлгомжтой тул
                tooltip хангалттай. */}
            <Tooltip title="Шүүлтийг цэвэрлэх">
              <Button
                icon={<i className="fas fa-eraser" />}
                aria-label="Шүүлтийг цэвэрлэх"
                onClick={() => {
                  setFTseh(null);
                  setFTsehParent(null);
                  setFNegj(null);
                  setFSeverity(null);
                  setFSearch("");
                }}
              />
            </Tooltip>
          </Col>
        </Row>

        {/* ⚠ 100-аас их мөр байвал ИЛ хэлнэ. Дуугүй тайрвал хэрэглэгч
            дарааллыг хоослосон гэж эндүүрнэ. */}
        {total > items.length ? (
          <div style={{ fontSize: 11.5, color: "#C2860B", marginTop: 8 }}>
            <i className="fas fa-info-circle" style={{ marginRight: 5 }} />
            Нийт {total} мөрөөс эхний {items.length} нь харагдаж байна — цех,
            хэсгээр шүүж нарийсгана уу.
          </div>
        ) : null}
      </Card>

      {/* Бөөнөөр хуваарилах — таб бүрд ӨӨР үйлдэл:
          «Нэгж тодорхойлох» → нэгжид · «Нэгжид хуваарилсан» → хүнд */}
      {picked.length &&
      (tab === "waiting" ? canUnitAssign : tab === "unit" && isUnitHse) ? (
        <Card
          size="small"
          style={{ marginBottom: 10, borderColor: BRAND + "55" }}
          bodyStyle={{ padding: "8px 12px" }}
        >
          <span style={{ marginRight: 12, fontWeight: 700 }}>
            {picked.length} мөр сонгогдсон
          </span>
          <Button
            type="primary"
            size="small"
            style={{ background: BRAND, borderColor: BRAND }}
            onClick={() =>
              tab === "waiting"
                ? openUnitAssign("bulk")
                : openAssign("bulk")
            }
          >
            {tab === "waiting"
              ? "Бөөнөөр нэгжид хуваарилах"
              : "Бөөнөөр хуваарилах"}
          </Button>
          <Button
            size="small"
            style={{ marginLeft: 8 }}
            onClick={() => setPicked([])}
          >
            Цуцлах
          </Button>
        </Card>
      ) : null}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : !items.length ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Эзэн хүлээж буй мэдээлэл алга — бүгд хуваарилагдсан байна."
          />
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((it) => {
            const sev = SEVERITY[it.severity] || SEVERITY[2];
            return (
              <Card
                key={it.id}
                size="small"
                style={{ borderRadius: 10, borderLeft: `3px solid ${sev.color}` }}
                bodyStyle={{ padding: "10px 14px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {/* ⚠ МӨРИЙН туг. Глобал тугаар бол бүлэг 179-д
                      бусад нэгжийн мөр дээр ч сонголт гарч, дарвал
                      403 авна — товч нь урхи болно. */}
                  {rowCanAct(it) ? (
                    <Checkbox
                      checked={picked.includes(it.id)}
                      onChange={(e) =>
                        setPicked((p) =>
                          e.target.checked
                            ? [...p, it.id]
                            : p.filter((x) => x !== it.id),
                        )
                      }
                    />
                  ) : null}
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {it.report_number || `#${it.id}`}
                  </span>
                  <Tag color={sev.color} style={{ marginInlineEnd: 0 }}>
                    {sev.name}
                  </Tag>
                  {tab === "waiting" ? <WaitedFor date={it.report_date} /> : null}
                  {/* Хуваарилсан табад — ХЭНД, ХЭЗЭЭ хүртэл, ЯМАР ажил */}
                  {tab === "assigned" ? (
                    <>
                      <span style={{ fontSize: 11.5, color: "#5A6478" }}>
                        <i
                          className="fas fa-user-check"
                          style={{ marginRight: 4 }}
                        />
                        {it.assigned_to_name || "—"}
                      </span>
                      {it.sla_deadline ? (
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            color:
                              new Date(it.sla_deadline) < new Date() &&
                              it.status !== "COMPLETED"
                                ? "#DC2626"
                                : "#5A6478",
                          }}
                        >
                          {String(it.sla_deadline).slice(0, 10)} хүртэл
                        </span>
                      ) : null}
                      {it.assigned_work_type ? (
                        <Tag
                          color={WORK_COLOR[it.assigned_work_type]}
                          style={{ marginInlineEnd: 0 }}
                        >
                          {WORK_LABEL[it.assigned_work_type]}
                          {it.assigned_work_job_id ? " · үүссэн" : " · хүлээгдэж буй"}
                        </Tag>
                      ) : null}
                    </>
                  ) : null}
                  <span style={{ marginLeft: "auto" }}>
                    {/* ХОЁР ШАТЛАЛТ: нэгж → хүн. Таб бүрд ӨӨР товч. */}
                    {tab === "waiting" && it.can_assign_unit ? (
                      <Button
                        type="primary"
                        size="small"
                        style={{ background: BRAND, borderColor: BRAND }}
                        onClick={() => openUnitAssign(it)}
                      >
                        <i
                          className="fas fa-building"
                          style={{ marginRight: 5 }}
                        />
                        Нэгжид хуваарилах
                      </Button>
                    ) : null}
                    {tab === "unit" && it.can_assign ? (
                      <Button
                        type="primary"
                        size="small"
                        style={{ background: BRAND, borderColor: BRAND }}
                        onClick={() => openAssign(it)}
                      >
                        <i
                          className="fas fa-user-check"
                          style={{ marginRight: 5 }}
                        />
                        Хүнд хуваарилах
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      style={{ marginLeft: 8 }}
                      onClick={() => navigate(`/hazard/${it.id}`)}
                    >
                      Дэлгэрэнгүй
                    </Button>
                  </span>
                </div>

                <div style={{ fontSize: 13, color: "#1F2430", marginTop: 6 }}>
                  {it.description || "—"}
                </div>
                {/* ── ХАРИУЦСАН НЭГЖ (2026-10-28) ─────────────────
                    «Хуваарилчаад хэн хэн рүү хуваарилагдсан нь
                     харагдахгүй байна» — тиймээс ГУРВАН табад ч
                     харуулна, зөвхөн «хуваарилсан»-д биш. */}
                {it.responsible_bn_name || it.responsible_bn_code ? (
                  <div style={{ marginTop: 5 }}>
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      <i className="fas fa-building" style={{ marginRight: 4 }} />
                      {it.responsible_bn_name || it.responsible_bn_code}
                    </Tag>
                  </div>
                ) : null}


                {/* ── ЯАЖ ШИЙДВЭРЛЭГДСЭН ──────────────────────────
                    `response_action` нь гүйцэтгэгчийн бичсэн арга
                    хэмжээ. Хурал дээр «шийдэгдсэн гэж байгаа, юу
                    хийснийг харуулахгүй» гэсэн гомдлыг хаана. */}
                {it.response_action ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#166534",
                      background: "#F0FDF4",
                      borderRadius: 6,
                      padding: "5px 8px",
                      marginTop: 5,
                    }}
                  >
                    <i className="fas fa-check-circle" style={{ marginRight: 5 }} />
                    {it.response_action}
                  </div>
                ) : null}

                <div style={{ fontSize: 11.5, color: "#8A94A6", marginTop: 3 }}>
                  {[it.insert_dept_name, it.location_text]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </div>

                {/* ── МЭДЭЭЛЭГЧТЭЙ ХОЛБОГДОХ (2026-10-28) ─────────
                    «Мэдээлэл өгсөн ажилтан бүртгэлийн дугаар,
                     бүтцийн нэгж, албан тушаал, утасны дугаар …
                     Зарим мэдээллийг тодруулах үүднээс эргээд
                     холбогдох шаардлага гардаг.»
                    ⚠ АНОНИМ бүртгэлд сервер утас, нэрийг БУЦААХГҮЙ
                      тул энэ мөр өөрөө алга болно. */}
                {it.insert_user_name ? (
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "#5A6478",
                      marginTop: 3,
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {/* ЦЭЭЖ ЗУРАГ — «Өмнөх 71300 программ шиг …
                        цээж зураг гардаг болох» (хэрэглэгч). */}
                    {it.insert_user_tn ? (
                      <img
                        src={`${EMP_PHOTO}${it.insert_user_tn}.jpg`}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                        style={{
                          width: 34,
                          height: 34,
                          objectFit: "cover",
                          borderRadius: "50%",
                          border: "1px solid #E6E9EF",
                          background: "#F1F3F6",
                        }}
                      />
                    ) : null}
                    <span>
                      <i className="fas fa-user" style={{ marginRight: 4 }} />
                      {it.insert_user_name}
                      {/* «Б/Д» — кодын сангийн нэгдсэн нэршил
                          (WorkerLookupSection, RemedySection). */}
                      {it.insert_user_tn ? ` · Б/Д ${it.insert_user_tn}` : ""}
                    </span>
                    {it.insert_user_position ? (
                      <span>{it.insert_user_position}</span>
                    ) : null}
                    {it.insert_user_phone ? (
                      <a
                        href={`tel:${it.insert_user_phone}`}
                        style={{ fontWeight: 700, color: BRAND }}
                      >
                        <i className="fas fa-phone" style={{ marginRight: 4 }} />
                        {it.insert_user_phone}
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Хуваарилах цонх ───────────────────────────────────────────
          ⚠ ХЭЛБЭРЭЭР салгана (`assignMode`): нэгжид хуваарилах нь ӨӨР
            endpoint, өөр эрх, мөн хугацаа/ажлын төрөл ШААРДАХГҮЙ. */}
      <Modal
        open={!!assignOf}
        onCancel={() => setAssignOf(null)}
        onOk={assignMode === "unit" ? doAssignUnit : doAssign}
        okText={
          assignMode === "unit" ? "Нэгжид хуваарилах" : "Хуваарилах"
        }
        cancelText="Болих"
        confirmLoading={busy}
        title={
          assignMode === "unit"
            ? assignOf === "bulk"
              ? `${picked.length} мэдээллийг нэгжид хуваарилах`
              : "Хариуцах нэгж тодорхойлох"
            : assignOf === "bulk"
            ? `${picked.length} мэдээллийг хуваарилах`
            : "Хариуцагч томилох"
        }
      >
        {assignOf && assignOf !== "bulk" ? (
          <div style={{ fontSize: 12.5, color: "#5A6478", marginBottom: 10 }}>
            <b>{assignOf.report_number}</b> · {assignOf.description}
          </div>
        ) : null}

        {/* ══ НЭГЖИД ХУВААРИЛАХ (шат 1 — ХАБ хэлтэс) ═════════════
            ⚠ ХУГАЦАА ба АЖЛЫН ТӨРӨЛ АСУУХГҮЙ: эзэн нь хараахан
              тодороогүй тул хугацаа тогтоох нь эрт. Тэднийг нэгжийн
              ХАБЭА хүнд хуваарилахдаа тавина. */}
        {assignMode === "unit" ? (
          <>
            {assignOf !== "bulk" && (aiLoading || aiSug || aiErr) ? (
              <div
                style={{
                  border: "1px solid #DDD6FE",
                  background: "#F5F3FF",
                  borderRadius: 8,
                  padding: "8px 10px",
                  marginBottom: 10,
                  fontSize: 12.5,
                }}
              >
                {aiLoading ? (
                  <span>
                    <Spin size="small" /> AI хариуцах нэгжийг тодорхойлж байна…
                  </span>
                ) : aiErr ? (
                  <span style={{ color: "#64748B" }}>{aiErr}</span>
                ) : aiSug ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <b style={{ color: "#6D28D9" }}>AI санал:</b>
                      <Button
                        size="small"
                        type={String(uTseh) === String(aiSug.unit_code) ? "primary" : "default"}
                        onClick={() => pickAiUnit(aiSug.unit_code, aiSug.unit_name)}
                      >
                        {aiSug.unit_name}
                      </Button>
                      <Tag color={aiSug.confidence >= 90 ? "green" : "orange"} style={{ marginRight: 0 }}>
                        {aiSug.confidence}%
                      </Tag>
                      {(aiSug.alternatives || []).map((a) => (
                        <Button
                          key={a.code}
                          size="small"
                          type={String(uTseh) === String(a.code) ? "primary" : "dashed"}
                          onClick={() => pickAiUnit(a.code, a.name)}
                        >
                          {a.name}
                        </Button>
                      ))}
                    </div>
                    {aiSug.reason ? (
                      <div style={{ color: "#5B5470", marginTop: 4 }}>{aiSug.reason}</div>
                    ) : null}
                    {(aiSug.examples || []).length ? (
                      <Tooltip
                        title={
                          <div>
                            {aiSug.examples.map((x, i) => (
                              <div key={i} style={{ marginBottom: 4 }}>
                                «{x.text}» → <b>{x.unit_name}</b>
                              </div>
                            ))}
                          </div>
                        }
                      >
                        <span style={{ color: "#7C3AED", cursor: "help", fontSize: 11.5 }}>
                          Төстэй өмнөх мэдээлэл ({aiSug.examples.length})
                        </span>
                      </Tooltip>
                    ) : null}
                    {aiSug.method === "knn" ? (
                      <div style={{ color: "#94A3B8", fontSize: 11 }}>
                        Gemini унтраалттай — зөвхөн төстэй мэдээллийн хуваарилалтаар
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
            <Row gutter={8} style={{ marginBottom: 10 }}>
              <Col xs={24} md={12}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Цех / хэлтэс
                </div>
                <DepartmentTseh
                  value={uTseh}
                  noAutoSelect
                  /* Хэрэглэгч (2026-11-05): «цех хэсэг код харагдаж
                     байна, нэр хэрэгтэй» — кодоор ХАЙХ нь хэвээр. */
                  nameOnly
                  /* ⚠⚠ 3-Р АРГУМЕНТ нь НЭР (`departmentname`), ОБЪЕКТ
                     БИШ — `departmentTseh.js:89` нь
                     `onChange(code, item.id, item.departmentname)` гэж
                     дууддаг. Өмнө нь `row.name` гэж уншиж байсан тул
                     `undefined` → `uName` ҮРГЭЛЖ null болж, серверт
                     зөвхөн КОД хадгалагдаж байв. Улмаас жагсаалт,
                     мэдэгдэлд «2505» гэсэн код харагдана (хэрэглэгч
                     2026-11-05: «цех хэсэг код харагдаж байна, нэр
                     хэрэгтэй»). */
                  onChange={(code, parentId, name) => {
                    setUTseh(code || null);
                    setUTsehParent(parentId || null);
                    setUNegj(null);
                    setUNegjName(null);
                    setUTsehName(name || null);
                  }}
                />
              </Col>
              <Col xs={24} md={12}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Хэсэг (заавал биш)
                </div>
                <DepartmentNegj
                  value={uNegj}
                  paranetid={uTsehParent}
                  parentCode={uTseh}
                  autoSelectOwn={false}
                  nameOnly
                  /* ⚠⚠ `departmentNegj.onChange(code, departmentname)` —
                     нэр нь ХОЁРДУГААР аргумент. Өмнө нь 3-дугаарыг
                     (`row`) уншиж байсан тул үргэлж `undefined` байв.
                     ⚠ Хэсгийг ЦЭВЭРЛЭХЭД нэрийг ч цэвэрлэнэ — эс
                       бөгөөс цех рүү буцахад хэсгийн нэр үлдэж,
                       буруу нэгжийн нэр хадгалагдана. */
                  onChange={(code, name) => {
                    setUNegj(code || null);
                    setUNegjName(code ? name || null : null);
                  }}
                />
              </Col>
            </Row>
            <div
              style={{
                fontSize: 11.5,
                color: "#5A6478",
                background: "#EFF6FF",
                borderRadius: 6,
                padding: "7px 9px",
                marginBottom: 10,
              }}
            >
              <i className="fas fa-info-circle" style={{ marginRight: 5 }} />
              Нэгжид хуваарилахад тэр нэгжийн <b>бүх ХАБЭА инженер</b>
              мэдээллийг харж, дотроо гүйцэтгэгчээ оноож чадна. Хэсэг
              сонгоогүй бол цех бүхэлдээ харна.
            </div>
          </>
        ) : null}

        {/* ══ ХҮНД ХУВААРИЛАХ (шат 2) ═══════════════════════════ */}
        {assignMode === "person" ? (
        <>
        {/* ЦЕХ → ХЭСЭГ → АЖИЛТАН гэсэн дараалал */}
        <Row gutter={8} style={{ marginBottom: 10 }}>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Цех
            </div>
            <DepartmentTseh
              value={tseh}
              noAutoSelect
              onChange={(code, parentId) => {
                setTseh(code || null);
                setTsehParent(parentId || null);
                // Цех солигдоход хэсэг ба сонгосон ажилтан ХҮЧИНГҮЙ —
                // эс бөгөөс өөр цехийн хүн сонгогдсон хэвээр үлдэнэ.
                setNegj(null);
                setTn(null);
                searchWorkers("", { tseh: code || null, negj: null });
              }}
            />
          </Col>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Хэсэг
            </div>
            <DepartmentNegj
              value={negj}
              paranetid={tsehParent}
              parentCode={tseh}
              autoSelectOwn={false}
              onChange={(code) => {
                setNegj(code || null);
                setTn(null);
                searchWorkers("", { negj: code || null });
              }}
            />
          </Col>
        </Row>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Хариуцагч
          </div>
          <Select
            showSearch
            style={{ width: "100%" }}
            value={tn}
            onChange={setTn}
            onSearch={(q) => searchWorkers(q)}
            filterOption={false}
            loading={wLoading}
            placeholder={
              tseh || negj
                ? "Жагсаалтаас сонгох (нэр, ТН-ээр хайж болно)"
                : "Эхлээд цех эсвэл хэсгээ сонгоно уу"
            }
            options={workerOptions}
            notFoundContent={
              wLoading ? (
                <Spin size="small" />
              ) : tseh || negj ? (
                "Ажилтан олдсонгүй"
              ) : (
                "Цех/хэсгээ сонгоно уу"
              )
            }
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Хугацаа (сар)
          </div>
          <DatePicker
            picker="month"
            style={{ width: "100%" }}
            value={due}
            onChange={setDue}
            format="YYYY оны M сар"
            placeholder="Сар сонгох"
            disabledDate={(d) => d && d.endOf("month") < dayjs().startOf("day")}
          />
          <div style={{ fontSize: 11, color: "#A8AFBC", marginTop: 4 }}>
            Хугацаа нь сонгосон САРЫН СҮҮЛИЙН ӨДӨР болно
            {due ? ` (${due.endOf("month").format("YYYY-MM-DD")})` : ""}.
            Ноцтой байдлаас санал болгосон — бодит нөхцөлд тааруулж өөрчилнө үү.
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Ажлын төрөл
          </div>
          <Select
            allowClear
            style={{ width: "100%" }}
            value={workType}
            onChange={(v) => setWorkType(v ?? null)}
            placeholder="Сонгохгүй бол зөвхөн «Миний даалгавар» дээр гарна"
            options={WORK_TYPES.map((w) => ({
              value: w.value,
              label: w.label,
            }))}
          />
          <div style={{ fontSize: 11, color: "#A8AFBC", marginTop: 4 }}>
            {workType
              ? WORK_TYPES.find((w) => w.value === workType)?.hint
              : "Сонговол хариуцагчийн бүртгэлийн хуудсанд «надад ирсэн ажил» болж гарна."}
          </div>
        </div>
        </>
        ) : null}

        <Input.TextArea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Тэмдэглэл (заавал биш)"
        />
        <div style={{ fontSize: 11, color: "#A8AFBC", marginTop: 6 }}>
          {assignMode === "unit"
            ? "Нэгж тодорхойлох нь төлөвийг хөндөхгүй — мэдээлэл нэгжийн ХАБЭА-гийн дараалалд орно."
            : "Хуваарилагдсан хүнд и-мэйлээр мэдэгдэнэ, мөн «Миний даалгавар» хуудсанд нь гарна."}
        </div>
      </Modal>
    </div>
  );
}
